import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { categoriesApi } from '@/services/api';
import { Category } from '@/types';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import ImageUpload from '@/components/ui/ImageUpload';
import { TagIcon, PlusIcon, XMarkIcon, PencilIcon, TrashIcon, LinkIcon, PhotoIcon, SparklesIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { suggestImageForCategory, searchImageSuggestions, CategoryImageSuggestion } from '@/utils/defaultCategoryImages';

const CategoryManagementPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    parent_id: '',
    is_active: true,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageMode, setImageMode] = useState<'url' | 'upload'>('url');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [imageSuggestions, setImageSuggestions] = useState<CategoryImageSuggestion[]>([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      // Pass true to include all categories (including subcategories)
      const response = await categoriesApi.getAll(true);
      setCategories(response.data);
    } catch (error: any) {
      toast.error('Failed to fetch categories');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const categoryData = {
        name: formData.name,
        description: formData.description,
        image_url: imageMode === 'url' ? (formData.image_url || null) : null,
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : null,
        is_active: formData.is_active,
      };

      let category: Category;

      if (editingCategory) {
        const response = await categoriesApi.update(editingCategory.id, categoryData);
        category = response.data;
        toast.success('Category updated successfully!');
      } else {
        const response = await categoriesApi.create(categoryData);
        category = response.data;
        toast.success('Category created successfully!');
      }

      // Upload image file if provided
      if (imageMode === 'upload' && imageFile && category) {
        try {
          await categoriesApi.uploadImage(category.id, imageFile);
          toast.success('Category image uploaded successfully!');
        } catch (error: any) {
          toast.error(error.response?.data?.detail || 'Failed to upload image');
        }
      }

      setShowCreateForm(false);
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        image_url: '',
        parent_id: '',
        is_active: true,
      });
      setImageFile(null);
      setImageMode('url');
      fetchCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || `Failed to ${editingCategory ? 'update' : 'create'} category`);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || '',
      parent_id: category.parent_id?.toString() || '',
      is_active: category.is_active ?? true,
    });
    setImageFile(null);
    // Determine image mode based on existing image
    if (category.image_url) {
      setImageMode(category.image_url.startsWith('/uploads/') ? 'upload' : 'url');
    } else {
      setImageMode('url');
    }
    setShowCreateForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }
    
    try {
      await categoriesApi.delete(id);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
      console.error(error);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      image_url: '',
      parent_id: '',
      is_active: true,
    });
    setImageFile(null);
    setImageMode('url');
    setShowSuggestions(false);
  };

  const handleGetSuggestions = () => {
    const suggestions = searchImageSuggestions(formData.name);
    setImageSuggestions(suggestions);
    setShowSuggestions(true);
  };

  const handleSelectSuggestion = (imageUrl: string) => {
    setFormData(prev => ({ ...prev, image_url: imageUrl }));
    setShowSuggestions(false);
    toast.success('Image suggestion applied!');
  };

  const mainCategories = categories.filter(cat => !cat.parent_id);
  const getSubCategories = (parentId: number) => {
    return categories.filter(cat => cat.parent_id === parentId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Category Management</h1>
            <p className="mt-2 text-gray-600">Manage categories and sub-categories</p>
          </div>
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {showCreateForm ? 'Cancel' : 'Create Category'}
          </Button>
        </div>

        {/* Create/Edit Category Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingCategory ? 'Edit Category' : 'Create New Category'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Category Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Smartphones"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Category description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Category Image
                </label>

                {/* Image Mode Toggle */}
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setImageMode('url')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      imageMode === 'url'
                        ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                    }`}
                  >
                    <LinkIcon className="h-4 w-4" />
                    External URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageMode('upload')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      imageMode === 'upload'
                        ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                    }`}
                  >
                    <PhotoIcon className="h-4 w-4" />
                    Upload File
                  </button>
                </div>

                {/* URL Input Mode */}
                {imageMode === 'url' && (
                  <div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Input
                          name="image_url"
                          value={formData.image_url}
                          onChange={handleInputChange}
                          placeholder="https://images.unsplash.com/photo-..."
                          helperText="Enter an image URL from Unsplash or any other source"
                        />
                      </div>
                      {formData.name && (
                        <Button
                          type="button"
                          variant="outline"
                          size="md"
                          onClick={handleGetSuggestions}
                          className="mt-0 flex items-center gap-2"
                        >
                          <SparklesIcon className="h-4 w-4" />
                          Suggest
                        </Button>
                      )}
                    </div>

                    {/* Image Suggestions */}
                    {showSuggestions && imageSuggestions.length > 0 && (
                      <div className="mt-4 p-4 border border-primary-200 rounded-lg bg-primary-50">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Suggested Images ({imageSuggestions.length})
                          </h4>
                          <button
                            type="button"
                            onClick={() => setShowSuggestions(false)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                          {imageSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => handleSelectSuggestion(suggestion.imageUrl)}
                              className="group relative aspect-video rounded-lg overflow-hidden border-2 border-transparent hover:border-primary-500 transition-all"
                            >
                              <img
                                src={suggestion.imageUrl}
                                alt={suggestion.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center">
                                <span className="text-white font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                  {suggestion.name}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {formData.image_url && !showSuggestions && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 mb-2">Image Preview:</p>
                        <div className="relative w-full h-48 rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={formData.image_url}
                            alt="Category preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Invalid+Image+URL';
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* File Upload Mode */}
                {imageMode === 'upload' && (
                  <ImageUpload
                    value={editingCategory?.image_url || null}
                    onChange={(file) => setImageFile(file)}
                    helperText="Upload a category image (JPEG, PNG, WebP, or GIF, max 5MB)"
                    maxSize={5}
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parent Category (Leave empty for main category)
                </label>
                <select
                  name="parent_id"
                  value={formData.parent_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">None (Main Category)</option>
                  {mainCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Active</label>
              </div>

              <div className="flex gap-4">
                <Button type="submit">{editingCategory ? 'Update Category' : 'Create Category'}</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Categories List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">All Categories</h2>
            
            <div className="space-y-6">
              {mainCategories.map((mainCategory) => {
                const subCategories = getSubCategories(mainCategory.id);
                return (
                  <div key={mainCategory.id} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {mainCategory.image_url ? (
                          <img
                            src={mainCategory.image_url}
                            alt={mainCategory.name}
                            className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center border border-gray-200">
                            <TagIcon className="h-6 w-6 text-primary-600" />
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-gray-900">{mainCategory.name}</h3>
                          {mainCategory.description && (
                            <span className="text-sm text-gray-500">- {mainCategory.description}</span>
                          )}
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            mainCategory.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {mainCategory.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(mainCategory)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(mainCategory.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    
                    {subCategories.length > 0 && (
                      <div className="ml-20 mt-3 space-y-2">
                        {subCategories.map((subCategory) => (
                          <div key={subCategory.id} className="flex items-center justify-between text-sm text-gray-700">
                            <div className="flex items-center gap-3">
                              {subCategory.image_url ? (
                                <img
                                  src={subCategory.image_url}
                                  alt={subCategory.name}
                                  className="w-10 h-10 rounded-md object-cover border border-gray-200"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <span className="w-2 h-2 bg-primary-400 rounded-full"></span>
                              )}
                              <div className="flex items-center gap-2 flex-wrap">
                                <span>{subCategory.name}</span>
                                {subCategory.description && (
                                  <span className="text-gray-500 text-xs">- {subCategory.description}</span>
                                )}
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  subCategory.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {subCategory.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEdit(subCategory)}
                                className="text-primary-600 hover:text-primary-900"
                                title="Edit"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(subCategory.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {subCategories.length === 0 && (
                      <p className="ml-7 text-sm text-gray-400 italic">No sub-categories</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagementPage;

